import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { clientAuthorizedGet } from 'utils/fetchUtils';
import { API_ROUTES, CACHE_KEYS } from 'utils/constants';
import {
  Company,
  CompanyWithOwnership,
  CreateCompanyRequest,
} from 'types/company';
import { decrypt, encrypt } from 'utils/cryptoUtils';

/**
 * Company API service
 * Consolidates all company-related API calls in a single location
 */
export function useCompanyServiceQueries() {
  const apiClient = useApiClient();

  /**
   * Get company by ID
   */
  const getCompanyById = useCallback(
    async (id: string): Promise<Company> => {
      return apiClient.get<Company>(`${API_ROUTES.COMPANY}/${id}`);
    },
    [apiClient],
  );

  /**
   * Get current user's company
   */
  const getCurrentCompany = useCallback(async (): Promise<Company | null> => {
    if (!apiClient.companyId) {
      return null;
    }
    return apiClient.get<Company>(
      `${API_ROUTES.COMPANY}/${apiClient.companyId}`,
    );
  }, [apiClient]);

  /**
   * Get all companies for the current user
   * Returns companies owned by or linked to the user
   */
  const getAllCompanies = useCallback(async (): Promise<
    CompanyWithOwnership[]
  > => {
    // Use clientAuthorizedGet directly without companyId to get all user's companies
    return clientAuthorizedGet<CompanyWithOwnership[]>(
      API_ROUTES.COMPANY_MY_COMPANIES,
      undefined,
      null,
    );
  }, []);

  /**
   * Create a new company
   */
  const createCompany = useCallback(
    async (company: CreateCompanyRequest): Promise<Company> => {
      return apiClient.post<Company>(
        API_ROUTES.COMPANY,
        company as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update a company
   */
  const updateCompany = useCallback(
    async (
      id: string,
      company: {
        name?: string;
        description?: string;
        address?: string;
        tin?: string;
        tel?: string;
        email?: string;
        vatStatus?: string;
        entityType?: string;
        registrationDate?: string;
        registrationNumber?: string;
        tradeName?: string;
        businessStyle?: string;
        isBmbe?: boolean;
        bmbeRegistrationNumber?: string;
        fiscalYearType?: 'calendar' | 'custom';
        fiscalYearStart?: string;
        fiscalYearEnd?: string;
      },
    ): Promise<Company> => {
      return apiClient.patch<Company>(`${API_ROUTES.COMPANY}/${id}`, company);
    },
    [apiClient],
  );

  /**
   * Delete a company
   */
  const deleteCompany = useCallback(
    async (id: string): Promise<{ removed: boolean }> => {
      const response = await apiClient.delete<{ removed: boolean }>(
        `${API_ROUTES.COMPANY}/${id}`,
      );
      return response ?? { removed: true };
    },
    [apiClient],
  );

  /**
   * Leave a company as a linked member.
   */
  const leaveCompany = useCallback(
    async (id: string): Promise<{ left: boolean }> => {
      const response = await apiClient.delete<{ left: boolean }>(
        `${API_ROUTES.COMPANY}/${id}/leave`,
      );
      return response ?? { left: true };
    },
    [apiClient],
  );

  /**
   * Link a user to a company via email
   */
  const linkUserToCompany = useCallback(
    async (id: string, email: string) => {
      return apiClient.post(`${API_ROUTES.COMPANY}/${id}/link-user`, { email });
    },
    [apiClient],
  );

  /**
   * List users linked to a company
   */
  const getCompanyUsers = useCallback(
    async (id: string) => {
      return apiClient.get<
        {
          id: string;
          email: string;
          username?: string;
          role?: 'owner' | 'member';
          owned?: boolean;
        }[]
      >(`${API_ROUTES.COMPANY}/${id}/users`);
    },
    [apiClient],
  );

  /**
   * Unlink a user from a company
   */
  const unlinkCompanyUser = useCallback(
    async (companyId: string, userId: string) => {
      return apiClient.delete(
        `${API_ROUTES.COMPANY}/${companyId}/users/${userId}`,
      );
    },
    [apiClient],
  );

  /**
   * Transfer company ownership to another linked user
   */
  const transferCompanyOwnership = useCallback(
    async (companyId: string, userId: string): Promise<Company> => {
      return apiClient.patch<Company>(
        `${API_ROUTES.COMPANY}/${companyId}/transfer-ownership`,
        { userId },
      );
    },
    [apiClient],
  );

  const getCompanyLogoUploadUrl = useCallback(
    async (
      companyId: string,
      payload: {
        filename: string;
        contentType?: string;
        expiresInSeconds?: number;
      },
    ): Promise<{ uploadUrl: string; path: string }> => {
      return apiClient.post<{ uploadUrl: string; path: string }>(
        `${API_ROUTES.COMPANY}/${companyId}/logo/upload-url`,
        payload as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  const setCompanyLogoPath = useCallback(
    async (
      companyId: string,
      payload: {
        path: string;
        filename?: string;
        size?: number;
        mimeType?: string;
      },
    ): Promise<{ logoPath: string | null }> => {
      return apiClient.patch<{ logoPath: string | null }>(
        `${API_ROUTES.COMPANY}/${companyId}/logo`,
        payload as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  const getCompanyLogoDownloadUrl = useCallback(
    async (companyId: string): Promise<{ downloadUrl: string }> => {
      return apiClient.get<{ downloadUrl: string }>(
        `${API_ROUTES.COMPANY}/${companyId}/logo/download-url`,
      );
    },
    [apiClient],
  );

  const removeCompanyLogo = useCallback(
    async (companyId: string): Promise<{ removed: boolean }> => {
      const response = await apiClient.delete<{ removed: boolean }>(
        `${API_ROUTES.COMPANY}/${companyId}/logo`,
      );
      return response ?? { removed: true };
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getCompanyById,
      getCurrentCompany,
      getAllCompanies,
      createCompany,
      updateCompany,
      deleteCompany,
      leaveCompany,
      linkUserToCompany,
      getCompanyUsers,
      unlinkCompanyUser,
      transferCompanyOwnership,
      getCompanyLogoUploadUrl,
      setCompanyLogoPath,
      getCompanyLogoDownloadUrl,
      removeCompanyLogo,
    }),
    [
      getCompanyById,
      getCurrentCompany,
      getAllCompanies,
      createCompany,
      updateCompany,
      deleteCompany,
      leaveCompany,
      linkUserToCompany,
      getCompanyUsers,
      unlinkCompanyUser,
      transferCompanyOwnership,
      getCompanyLogoUploadUrl,
      setCompanyLogoPath,
      getCompanyLogoDownloadUrl,
      removeCompanyLogo,
    ],
  );
}

export const useCompanyService = () => {
  /**
   * Encrypt the company id
   */
  const encryptCompanyId = async (companyId: string) => {
    return await encrypt(companyId);
  };

  /**
   * Decrypt the company id
   */
  const decryptCompanyId = async (companyId: string) => {
    return decrypt(companyId);
  };

  const setActiveCompanyIdInLocalStorage = async (companyId: string) => {
    const getEncryptedCompanyId = async () => {
      return await encryptCompanyId(companyId);
    };

    const encryptedCompanyId = await getEncryptedCompanyId();
    localStorage.setItem(CACHE_KEYS.ACTIVE_COMPANY_ID, encryptedCompanyId);
  };

  const getActiveCompanyIdFromLocalStorage = async () => {
    const encryptedCompanyId = localStorage.getItem(
      CACHE_KEYS.ACTIVE_COMPANY_ID,
    );
    return encryptedCompanyId
      ? await decryptCompanyId(encryptedCompanyId)
      : null;
  };

  return {
    encryptCompanyId,
    decryptCompanyId,
    setActiveCompanyIdInLocalStorage,
    getActiveCompanyIdFromLocalStorage,
  };
};
